import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Search } from "lucide-react";
import { paths } from "@shared-core/routing/routes";
import {
  SUPPLIER_GOODS_IN_TABS,
  type GoodsReceivedResult,
  type SupplierGoodsInRow,
  type SupplierGoodsInSort,
  type SupplierGoodsInTab,
} from "@shared-core/data/supplier-goods-in-adapter";
import { useSupplierGoodsInAdapter } from "@shared-core/data/SupplierPortalAdapterContext";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { useFieldErrorWrite } from "@shared-core/domain/useFieldErrorWrite";
import { useLoad } from "@shared-core/domain/useLoad";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  ACTION_WORDING,
  GOODS_IN_WORDING,
  STAFF_VIEW_WORDING,
  dayMonth,
  goodsInQuantity,
  goodsInSender,
  goodsInStatus,
  goodsInTabCountTone,
  goodsInTabLabel,
  platformToday,
} from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { Input } from "../../app/components/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { cn } from "../../app/components/primitives/utils";
import { StatusPill } from "../../app/components/domain/StatusPill";
import { StaffViewReadOnlyNote } from "../../app/components/domain/StaffViewReadOnly";
import { FormField } from "../../app/components/patterns/FormField";
import { CountBadge } from "../shell/SignedInShell";
import { useSupplierSession } from "../shell/session";
import {
  STAFF_VIEW_READ_ONLY_NOTE_ID,
  useIsStaffView,
} from "../shell/staffView";
import { EmptyState, LoadFailed, LoadingCard } from "../shell/states";
import {
  emptyGoodsInFields,
  foldGoodsInErrors,
  isReceivable,
  receiptEntries,
  startingQuantity,
  type GoodsInFields,
} from "./goodsInFields";
import { GoodsInResults } from "./GoodsInResults";

const PAGE_SIZE = 25;
/** The server's own order (earliest expected first, unknown last, then the oldest order) is the default: no param. */
const DEFAULT_SORT = "default";
const SORT_CHOICES: { value: string; label: string }[] = [
  { value: DEFAULT_SORT, label: GOODS_IN_WORDING.sortEarliest },
  { value: "-expected_on", label: GOODS_IN_WORDING.sortLatest },
  { value: "created_at", label: GOODS_IN_WORDING.sortOldest },
];

function tabFrom(value: string | null): SupplierGoodsInTab {
  return SUPPLIER_GOODS_IN_TABS.find((t) => t === value) ?? "due";
}

function sortFrom(value: string | null): string {
  return SORT_CHOICES.find((s) => s.value === value)?.value ?? DEFAULT_SORT;
}

/**
 * `/goods-in` (login only; spec 22 §6): what is due into a warehouse the supplier operates, by tab (Due in / Received
 * today / All), searchable, the earliest expected first. On Due in, ticking rows and choosing Record received records
 * them together with one reference and date and a quantity per row (all that is still due to start with); each row is
 * recorded on its own, so the result says which went through and why any did not. Filtering, search, sort and paging
 * are the server's (docs/list-endpoints.md); the page only renders.
 */
export function GoodsInPage() {
  const goodsIn = useSupplierGoodsInAdapter();
  const sessionState = useSupplierSession();
  const isStaffView = useIsStaffView();
  const warehouses = sessionState?.session.warehouses ?? [];
  const [params, setParams] = useSearchParams();
  const warehouseId = params.get("warehouse") ?? warehouses[0]?.id ?? null;
  const warehouseName =
    warehouses.find((w) => w.id === warehouseId)?.name ?? null;
  const tab = tabFrom(params.get("tab"));
  const sort = sortFrom(params.get("sort"));
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [query, setQuery] = useState(params.get("q") ?? "");
  const q = useDebouncedValue(query.trim(), 300);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [fields, setFields] = useState<GoodsInFields>(emptyGoodsInFields(""));
  const [outcome, setOutcome] = useState<{
    results: GoodsReceivedResult[];
    lineRefs: Record<string, string>;
  } | null>(null);
  const write = useFieldErrorWrite("We couldn't send this to Pollards.");

  // A new search starts again at page 1 (list-endpoints contract).
  useEffect(() => {
    if ((params.get("q") ?? "") === q) return;
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (q) next.set("q", q);
        else next.delete("q");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [q, params, setParams]);

  const listKey = `${warehouseId}|${tab}|${sort}|${q}|${page}`;
  const list = useLoad(
    () =>
      goodsIn.listGoodsIn({
        warehouseId,
        tab,
        sort: sort === DEFAULT_SORT ? undefined : (sort as SupplierGoodsInSort),
        q: q || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    listKey,
  );

  // What was ticked belongs to the rows it was ticked on.
  const resetWrite = write.reset;
  useEffect(() => {
    setSelected(new Set());
    setQuantities({});
    setIsFormOpen(false);
    resetWrite();
  }, [listKey, resetWrite]);

  const searchWith = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    }
    next.delete("page");
    return next;
  };
  const tabHref = (next: SupplierGoodsInTab) => {
    const search = searchWith({ tab: next === "due" ? null : next });
    const text = search.toString();
    return text ? `?${text}` : paths.supplierGoodsIn();
  };
  const pageHref = (n: number) => {
    const search = new URLSearchParams(params);
    if (n > 1) search.set("page", String(n));
    else search.delete("page");
    return `?${search.toString()}`;
  };

  const items = list.data?.items ?? [];
  const selectable = items.filter(isReceivable);
  const chosen = items.filter((row) => selected.has(row.delivery_line_id));
  const allSelected =
    selectable.length > 0 &&
    selectable.every((row) => selected.has(row.delivery_line_id));
  const today = list.data?.today ?? platformToday();

  const toggle = (row: SupplierGoodsInRow) => {
    const isOn = selected.has(row.delivery_line_id);
    setSelected((current) => {
      const next = new Set(current);
      if (isOn) next.delete(row.delivery_line_id);
      else next.add(row.delivery_line_id);
      return next;
    });
    if (!isOn) {
      setQuantities((current) => ({
        ...current,
        [row.delivery_line_id]: startingQuantity(row),
      }));
    }
  };
  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(selectable.map((row) => row.delivery_line_id)));
    setQuantities(
      Object.fromEntries(
        selectable.map((row) => [row.delivery_line_id, startingQuantity(row)]),
      ),
    );
  };

  const errors = foldGoodsInErrors(write.fieldErrors, chosen);
  const submit = () =>
    void write.run(async () => {
      const results = await goodsIn.recordGoodsReceived(
        receiptEntries(chosen, quantities, fields),
      );
      setOutcome({
        results,
        lineRefs: Object.fromEntries(
          chosen.map((row) => [row.delivery_line_id, row.line_ref]),
        ),
      });
      setSelected(new Set());
      setIsFormOpen(false);
      list.reload();
      sessionState?.refresh();
    });

  if (warehouses.length === 0) {
    return (
      <Card>
        <EmptyState title={GOODS_IN_WORDING.noWarehouses}>
          <p>{GOODS_IN_WORDING.noWarehousesWhy}</p>
        </EmptyState>
      </Card>
    );
  }

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold">Goods in</h1>
        {warehouses.length > 1 && warehouseId && (
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {GOODS_IN_WORDING.warehouse}
            </span>
            <Select
              value={warehouseId}
              onValueChange={(next) =>
                setParams(searchWith({ warehouse: next }))
              }
            >
              <SelectTrigger
                size="sm"
                className="w-40"
                aria-label={GOODS_IN_WORDING.warehouse}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {warehouses.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-x-1 gap-y-3">
        <nav
          aria-label="Goods in status"
          className="flex gap-1 overflow-x-auto"
        >
          {SUPPLIER_GOODS_IN_TABS.map((t) => {
            const n = list.data?.tabCounts[t];
            return (
              <Link
                key={t}
                to={tabHref(t)}
                aria-current={t === tab ? "page" : undefined}
                className={cn(
                  "border-b-2 px-2.5 py-2 text-sm whitespace-nowrap",
                  t === tab
                    ? "border-primary font-semibold text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {goodsInTabLabel(t)}
                {n !== undefined && n > 0 && (
                  <CountBadge
                    n={n}
                    tone={goodsInTabCountTone(t)}
                    label={`${n} deliveries`}
                  />
                )}
              </Link>
            );
          })}
        </nav>
        <span className="flex-1" />
        <Select
          value={sort}
          onValueChange={(next) =>
            setParams(searchWith({ sort: next === DEFAULT_SORT ? null : next }))
          }
        >
          <SelectTrigger
            size="sm"
            className="w-48"
            aria-label={GOODS_IN_WORDING.sortLabel}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_CHOICES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="relative w-full sm:w-64">
          <span className="sr-only">{GOODS_IN_WORDING.searchPlaceholder}</span>
          <Search
            aria-hidden
            className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-muted-foreground"
          />
          <Input
            type="search"
            className="pl-8"
            placeholder={GOODS_IN_WORDING.searchPlaceholder}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
      </div>

      {outcome && (
        <GoodsInResults
          results={outcome.results}
          lineRefs={outcome.lineRefs}
          onDismiss={() => setOutcome(null)}
        />
      )}

      {selected.size > 0 && (
        <Card className="gap-3 p-3">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm font-semibold" role="status">
              {GOODS_IN_WORDING.selectedCount(selected.size)}
            </p>
            <Button
              size="sm"
              aria-expanded={isFormOpen}
              onClick={() => {
                if (!isFormOpen && fields.date === "") {
                  setFields(emptyGoodsInFields(today));
                }
                setIsFormOpen(!isFormOpen);
              }}
            >
              {`${ACTION_WORDING.recordReceived} for ${selected.size}`}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelected(new Set())}
            >
              {ACTION_WORDING.cancel}
            </Button>
          </div>
          {isFormOpen && (
            <form
              noValidate
              aria-label={GOODS_IN_WORDING.bulkTitle}
              className="grid gap-3 rounded-lg border bg-card p-3"
              onSubmit={(event) => {
                event.preventDefault();
                submit();
              }}
            >
              <p className="text-sm font-semibold">
                {GOODS_IN_WORDING.bulkTitle}
              </p>
              <p className="text-xs text-muted-foreground">
                {GOODS_IN_WORDING.bulkHint}
              </p>
              {write.banner && (
                <p role="alert" className="text-sm text-red-600">
                  {write.banner}
                </p>
              )}
              {errors.unplaced.map((message) => (
                <p key={message} className="text-sm text-red-600">
                  {message}
                </p>
              ))}
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField
                  id="goods-in-ref"
                  label={GOODS_IN_WORDING.goodsInRef}
                  hint={GOODS_IN_WORDING.goodsInRefHint}
                  error={errors.form.goods_in_ref}
                >
                  {(control) => (
                    <Input
                      {...control}
                      value={fields.ref}
                      autoComplete="off"
                      onChange={(event) =>
                        setFields({ ...fields, ref: event.target.value })
                      }
                    />
                  )}
                </FormField>
                <FormField
                  id="goods-in-date"
                  label={GOODS_IN_WORDING.receivedOn}
                  error={errors.form.received_on}
                >
                  {(control) => (
                    <Input
                      {...control}
                      type="date"
                      max={today}
                      value={fields.date}
                      onChange={(event) =>
                        setFields({ ...fields, date: event.target.value })
                      }
                    />
                  )}
                </FormField>
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={write.isBusy}>
                  {ACTION_WORDING.recordReceived}
                </Button>
              </div>
            </form>
          )}
        </Card>
      )}

      {list.data ? (
        items.length === 0 ? (
          <Card>
            <EmptyState
              title={
                q
                  ? GOODS_IN_WORDING.noMatch(q)
                  : GOODS_IN_WORDING.empty(tab, warehouseName)
              }
            />
          </Card>
        ) : (
          <>
            {isStaffView && selectable.length > 0 && (
              <StaffViewReadOnlyNote>
                {STAFF_VIEW_WORDING.goodsReceived}
              </StaffViewReadOnlyNote>
            )}
            <GoodsInTable
              items={items}
              withSelection={selectable.length > 0}
              selected={selected}
              allSelected={allSelected}
              quantities={quantities}
              rowErrors={errors.rows}
              onToggle={toggle}
              onToggleAll={toggleAll}
              onQuantity={(id, value) =>
                setQuantities((current) => ({ ...current, [id]: value }))
              }
            />
            {list.data.totalPages > 1 && (
              <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
                Page {list.data.page} of {list.data.totalPages}
                {page > 1 && (
                  <Button asChild variant="outline" size="sm">
                    <Link to={pageHref(page - 1)}>Previous</Link>
                  </Button>
                )}
                {page < list.data.totalPages && (
                  <Button asChild variant="outline" size="sm">
                    <Link to={pageHref(page + 1)}>Next</Link>
                  </Button>
                )}
              </div>
            )}
          </>
        )
      ) : list.error ? (
        <LoadFailed
          what="your goods in"
          message={mutationErrorMessage(
            list.error,
            "Something went wrong reaching Pollards.",
          )}
          onRetry={list.reload}
        />
      ) : (
        <LoadingCard what="your goods in" />
      )}
    </div>
  );
}

function GoodsInTable({
  items,
  withSelection,
  selected,
  allSelected,
  quantities,
  rowErrors,
  onToggle,
  onToggleAll,
  onQuantity,
}: {
  items: SupplierGoodsInRow[];
  withSelection: boolean;
  selected: Set<string>;
  allSelected: boolean;
  quantities: Record<string, string>;
  rowErrors: Record<string, string>;
  onToggle: (row: SupplierGoodsInRow) => void;
  onToggleAll: () => void;
  onQuantity: (deliveryLineId: string, value: string) => void;
}) {
  const hasReceiving = items.some((row) => selected.has(row.delivery_line_id));
  // A staff view draws the ticks switched off, so the Record received bar never opens.
  const isStaffView = useIsStaffView();
  return (
    <Card className="gap-0 overflow-x-auto py-0">
      <Table>
        <TableHeader>
          <TableRow>
            {withSelection && (
              <TableHead className="w-8">
                <input
                  type="checkbox"
                  className="size-4 accent-primary"
                  aria-label="Select all deliveries on this page"
                  disabled={isStaffView}
                  aria-describedby={
                    isStaffView ? STAFF_VIEW_READ_ONLY_NOTE_ID : undefined
                  }
                  checked={allSelected}
                  onChange={onToggleAll}
                />
              </TableHead>
            )}
            <TableHead>{GOODS_IN_WORDING.columnReference}</TableHead>
            <TableHead>{GOODS_IN_WORDING.columnItem}</TableHead>
            <TableHead>{GOODS_IN_WORDING.columnFrom}</TableHead>
            <TableHead>{GOODS_IN_WORDING.columnExpected}</TableHead>
            <TableHead className="text-right">
              {GOODS_IN_WORDING.columnQuantity}
            </TableHead>
            {hasReceiving && (
              <TableHead>{GOODS_IN_WORDING.columnReceiving}</TableHead>
            )}
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((row) => {
            const status = goodsInStatus(row);
            const sender = goodsInSender(row);
            const id = row.delivery_line_id;
            const isOn = selected.has(id);
            const error = rowErrors[id];
            return (
              <TableRow key={id}>
                {withSelection && (
                  <TableCell>
                    <input
                      type="checkbox"
                      className="size-4 accent-primary"
                      aria-label={GOODS_IN_WORDING.selectRow(row.line_ref)}
                      checked={isOn}
                      disabled={isStaffView || !isReceivable(row)}
                      aria-describedby={
                        isStaffView ? STAFF_VIEW_READ_ONLY_NOTE_ID : undefined
                      }
                      onChange={() => onToggle(row)}
                    />
                  </TableCell>
                )}
                <TableCell className="font-mono text-xs">
                  {row.line_ref}
                </TableCell>
                <TableCell className="whitespace-normal">
                  <span className="font-mono text-xs">{row.stock_code}</span>
                  <span className="block text-muted-foreground">
                    {row.description}
                  </span>
                </TableCell>
                <TableCell className="whitespace-normal">
                  {sender.name}
                  {sender.detail && (
                    <span className="block text-xs text-muted-foreground">
                      {sender.detail}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {row.expected_on
                    ? dayMonth(row.expected_on)
                    : GOODS_IN_WORDING.unknownDate}
                </TableCell>
                <TableCell className="text-right">
                  {goodsInQuantity(row)}
                </TableCell>
                {hasReceiving && (
                  <TableCell>
                    {isOn && (
                      <>
                        <Input
                          type="number"
                          min={1}
                          className="w-24"
                          aria-label={GOODS_IN_WORDING.quantityFor(
                            row.line_ref,
                          )}
                          aria-invalid={error ? true : undefined}
                          aria-describedby={error ? `${id}-error` : undefined}
                          value={quantities[id] ?? startingQuantity(row)}
                          onChange={(event) =>
                            onQuantity(id, event.target.value)
                          }
                        />
                        {error && (
                          <p
                            id={`${id}-error`}
                            className="mt-1 text-xs text-red-600"
                          >
                            {error}
                          </p>
                        )}
                      </>
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <StatusPill label={status.label} tone={status.tone} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}
